import PropTypes from 'prop-types'
import React from 'react'
import classnames from 'classnames'
import { withStyles } from '@material-ui/core/styles'

// Components
import Button from '@material-ui/core/Button'
import Close from '@material-ui/icons/Close'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'
import IconButton from '@material-ui/core/IconButton'
import LinearProgress from '@material-ui/core/LinearProgress'
import LockIcon from '@material-ui/icons/Lock'
import Typography from '@material-ui/core/Typography'

// Hooks
import { useSidebarState } from 'hooks'

// Icons
import { CheckmarkIcon, DeleteIcon } from 'Components/Icons'

// Styles
import styles from './styles'

const iconMap = {
  changePassword: LockIcon,
  delete: DeleteIcon,
  error: DeleteIcon,
  success: CheckmarkIcon,
}

const IconComponent = ({ type }) => {
  const Component = iconMap[type]
  return <Component color="primary" width={80} height={80} />
}

IconComponent.propTypes = {
  type: PropTypes.string.isRequired,
}

const actionButtonTextMap = {
  error: 'Close',
  delete: 'Confirm',
  success: 'Ok',
}

const DialogBox = ({
  actionButtonText,
  body,
  cancelButtonText,
  classes,
  customClasses,
  disableButton,
  loading,
  onAction,
  onClose,
  onExited,
  open,
  showActions,
  showProgress,
  text,
  title,
  type,
  isSignUpDialog,
}) => {
  const { sidebarOpen } = useSidebarState()

  return (
    <Dialog
      aria-describedby="alert-dialog-description"
      aria-labelledby="alert-dialog-title"
      classes={{
        root: classnames(
          classes.root,
          {
            [classes.shiftRight]: sidebarOpen,
          },
          isSignUpDialog && classes.signUpDialogRoot,
        ),
        paperScrollPaper: classes.scrollPaper,
      }}
      fullWidth
      onClose={onClose}
      open={open}
      onExited={onExited}
      container={document.getElementById('root')}
    >
      {showProgress && (
        <div className={classes.loaderContainer}>
          <LinearProgress className={classes.loader} color="primary" variant="indeterminate" />
        </div>
      )}
      {showActions && (
        <div className={classes.close}>
          <IconButton onClick={onClose} className={classes.closeButton} size="small">
            <Close />
          </IconButton>
        </div>
      )}
      {title && (
        <DialogTitle
          id="alert-dialog-title"
          classes={{ root: classnames(classes.title, customClasses?.title) }}
        >
          {type && <IconComponent type={type} />}
          <Typography className={classes.type}>{title}</Typography>
        </DialogTitle>
      )}
      <DialogContent classes={{ root: classes.content }}>
        {body}
        {text && <DialogContentText id="alert-dialog-description">{text}</DialogContentText>}
      </DialogContent>
      {showActions && (
        <DialogActions classes={{ root: classes.actions }}>
          {cancelButtonText && (
            <Button autoFocus color="primary" onClick={onClose} variant="outlined">
              {cancelButtonText}
            </Button>
          )}
          <Button
            color="primary"
            disabled={loading || disableButton}
            onClick={onAction}
            variant="contained"
          >
            {actionButtonText || actionButtonTextMap[type]}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  )
}

DialogBox.defaultProps = {
  loading: false,
  showActions: true,
}

DialogBox.propTypes = {
  actionButtonText: PropTypes.string,
  body: PropTypes.node,
  cancelButtonText: PropTypes.string,
  classes: PropTypes.object.isRequired,
  customClasses: PropTypes.object,
  disableButton: PropTypes.bool,
  loading: PropTypes.bool,
  onAction: PropTypes.func,
  onClose: PropTypes.func,
  onExited: PropTypes.func,
  open: PropTypes.bool.isRequired,
  showActions: PropTypes.bool,
  showProgress: PropTypes.bool,
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  type: PropTypes.string,
}

export default withStyles(styles)(DialogBox)
